/* eslint-disable operator-linebreak */

import Cookies from 'js-cookie';
import { markStart, markEnd, clearMark } from 'sheinq';

import { prodOrigin, laneDomain, APMINFO } from './config';
import styles from './styles/index.less';
import { isLinkToUlp, linkToUlp, createIframeSrc, ulpRedirect } from './utils';
import { logout } from './service';
import { Singleton } from './utils/decorator';

export type EnvType = 'prod' | 'dev' | 'test' | 'sit' | 'local' | 'lane';

interface Props {
  /**
   * ULP 域名
   */
  ulpOrigin: string;
  /**
   * 是否显示遮罩
   */
  showMask?: boolean;
  /**
   * 系统所在环境
   */
  env?: EnvType;
  /**
   * 业务系统自定义接口配置
   */
  authOptions?: {
    /**
     * 业务自定义鉴权接口地址
     */
    url: string;
    /**
     * ulp token要写入的domain
     */
    domain?: string;
    /**
     * cookie name
     */
    cookieName?: string;
  };
  /**
   * 登陆接口地址,会拼接到当前系统的url后面
   */
  loginPath?: string;
  /**
   * 登录成功的回调函数
   */
  successCallback?: (token: string | null) => void;
  /**
   * 登录失败的回调函数
   */
  failCallback?: (error: Error) => void;
  /**
   * ulp未登陆时，加载ulp前
   */
  beforeVisitUlp?: (closeIframe: () => void) => void;
  /**
   * 是否前端登陆
   */
  fontendLogin?: boolean;
}

export type MaskCreateFunction = () => () => void;

export type MaskClear = () => void;

export class Mask {
  private config!: Props;

  private iframe?: HTMLIFrameElement = undefined;

  private mask?: HTMLElement = undefined;

  private isVisitedUlp = false;

  private ulpOrigin = '';

  private iframeSrc = '';

  private isUlpExpired = false;

  private clearMask: MaskClear | null = null;

  private maskCreateFn: MaskCreateFunction | null = null;

  // eslint-disable-next-line no-use-before-define
  private static instance: Mask;

  private isLoad = false;

  constructor(config: Props) {
    this.config = config;
    const { ulpOrigin, env, loginPath, authOptions } = config;
    this.ulpOrigin = ulpOrigin ?? prodOrigin;

    // const checkEnv = env === 'lane' ? 'local' : env;
    this.iframeSrc = createIframeSrc({
      ulpOrigin: this.ulpOrigin,
      env,
      needGetUlpToken: !!authOptions,
      loginPath,
    });

    window.addEventListener('message', this.postMessageToUrl, false);
  }

  private createIframe(): void {
    this.iframe = document.createElement('iframe');
    this.iframe.className = styles.hide;
    // 允许iframe内指纹识别
    this.iframe.allow = 'publickey-credentials-get *';

    this.iframe.src = this.iframeSrc;
    document.body.appendChild(this.iframe);
  }

  private createMask(): () => void {
    if (!this.maskCreateFn) this.maskCreateFn = this.defaultMaskCreate;
    if (!(this.config.showMask ?? true)) return () => null;
    return this.maskCreateFn();
  }

  private defaultMaskCreate: MaskCreateFunction = () => {
    this.mask = document.createElement('div');
    this.mask.className = styles['loading-wrap'];
    this.createDefaultLoading();
    document.body.appendChild(this.mask);
    return () => {
      if (this.mask) {
        document.body.removeChild(this.mask);
        this.mask = undefined;
      }
    };
  };

  private createDefaultLoading(): void {
    // 创建loading标志
    const loading = document.createElement('div');
    loading.className = styles['spin-default'];

    for (let index = 0; index < 12; ++index) {
      const wrap = document.createElement('div');
      wrap.className = styles['spin-default-item'];
      const div = document.createElement('div');
      wrap.appendChild(div);
      loading.appendChild(wrap);
    }

    this.mask?.appendChild(loading);
  }

  private destroyIframe(): void {
    // 销毁 iframe
    if (this.iframe) {
      document.body.removeChild(this.iframe);
      this.iframe = undefined;
    }
  }

  private fullScreenIframe = () => {
    const { iframe } = this;
    if (!iframe) return;
    iframe.className = styles.fullscreen;
  };

  private logoutMethod = async (callback?: () => void) => {
    // const isLoginBySso = getStorage();
    await logout(this.ulpOrigin);
    await callback?.();
  };

  private static setCookieForLane = (
    token: string,
    domain = laneDomain,
    cookieName = 'laneCookie',
  ) => {
    Cookies.set(cookieName, token, {
      domain,
    });
  };

  private static clearCookie = (cookieName = 'laneCookie') => {
    Cookies.remove(cookieName);
  };

  private postMessageToUrl = async ({
    data,
  }: {
    data: {
      type: string;
      token?: string;
    };
  }) => {
    const { ulpOrigin, fontendLogin, authOptions, loginPath } = this.config;
    const { type, token } = data;

    this.iframe?.contentWindow?.postMessage(
      {
        type: 'SSOSDK_INIT',
      },
      '*',
    );

    // iframe 展示 ulp
    if (type === 'SHEIN_AUTH_READY') {
      this.iframe?.contentWindow?.postMessage(
        {
          type: 'WORMHOLE_SSO',
        },
        '*',
      );
    }

    if (type === 'ULP_TOKEN_GET') {
      // ulp登陆成功后, cookie未写入成功时, 将token作为参数传递给sso接口
      this.systemLoginRetry(token!);
    }
    // 无痕禁用第三方cookie走老单点登陆
    if (type === 'INCOGNITO_ULP_ACCESS_STORAGE_BANNED') {
      linkToUlp(ulpOrigin, fontendLogin);
    }

    // 泳道情况时，在当前域写入ulp cookie,调用泳道接口后携带
    if (type === 'ULP_TOKEN_FOR_SOTEST_LANE') {
      if (!token) return;

      const { url, domain, cookieName } = authOptions ?? {};

      if (domain || cookieName) {
        Mask.setCookieForLane(token, domain, cookieName);
      }

      const srcWithToken = createIframeSrc({
        ulpOrigin: this.ulpOrigin,
        authUrl: url,
        loginPath,
      }).replace(/token=[^&]+/, `token=${token}`);

      this.iframe!.src = srcWithToken;
    }
  };

  private static isIframeCreate(): boolean {
    try {
      return (
        window.parent !== window.self &&
        window.self.location.hostname === window.parent.location.hostname
      );
    } catch (e) {
      return false;
    }
  }

  private handleError() {
    this.isUlpExpired = true;
    if (!this.isVisitedUlp) {
      this.isVisitedUlp = true;
      this.clearMask?.();
      this.config?.beforeVisitUlp?.(this.destroy);
      this.fullScreenIframe();
    }
  }

  private systemLoginRetry(token: string) {
    if (!this.iframe) return;
    const srcWithToken = this.iframeSrc.replace(/token=[^&]+/, `token=${token}`);
    this.clearMask = this.createMask();
    this.isVisitedUlp = false;
    this.iframe.src = srcWithToken;
    this.iframe.className = styles.hide;
  }

  public startSSO(): void {
    // 企微唤醒逻辑
    if (Mask.isIframeCreate() || this.isLoad) return;

    if (isLinkToUlp(this.ulpOrigin)) {
      ulpRedirect(this.ulpOrigin);
    }

    // 仅生成一次遮罩
    this.isLoad = true;

    // // 由sso登陆, 存储在storage中
    // setStorage();
    // 创建遮罩
    this.clearMask = this.createMask();

    // 创建 iframe
    this.createIframe();

    this.startBuryPoint();

    const { iframe } = this;
    if (!iframe) return;

    iframe.onload = async () => {
      try {
        // 判断同域
        const iframeHref = new URL(this.iframe?.contentWindow?.location?.href ?? '');
        const token = iframeHref?.searchParams?.get('token');
        await this.config?.successCallback?.(token);
        this.endBuryPoint();
        this.clearMask?.();
        this.destroy();
      } catch (error) {
        // ulp,未登陆访问ulp登陆页
        this.handleError();
      }
    };
  }

  private startBuryPoint() {
    const { LABEL, ULP_EXPIRED_LABEL, ID, EXPIRED_TIME } = APMINFO;
    markStart(ULP_EXPIRED_LABEL, {}, { id: ID });
    markStart(LABEL, {}, { id: ID });
    setTimeout(() => {
      this.isUlpExpired = false;
      clearMark(LABEL);
      clearMark(ULP_EXPIRED_LABEL);
    }, EXPIRED_TIME);
  }

  private endBuryPoint() {
    const { LABEL, ULP_EXPIRED_LABEL, ID } = APMINFO;

    if (this.isUlpExpired) {
      markEnd(ULP_EXPIRED_LABEL, {}, { id: ID });
      clearMark(LABEL);

      // if (window.sheinq) {
      //   window.sheinq.push('pv -f', {});
      // }
      this.isUlpExpired = false;
    } else {
      markEnd(LABEL, {}, { id: ID });
      clearMark(ULP_EXPIRED_LABEL);
    }
  }

  public destroy() {
    this.destroyIframe();
    this.isLoad = false;
    this.isVisitedUlp = false;
    this.clearMask = null;
  }

  public setMaskCreateFn(fn: MaskCreateFunction): void {
    this.maskCreateFn = fn;
  }

  public ulpLogout(callback?: () => void): void {
    const { iframe } = this;
    if (iframe) return;
    this.logoutMethod(callback);
  }
}
